import type { ReactElement, ReactNode } from 'react';

/**
 * Pure-CSS iPhone-style bezel for the embeddable widget. Renders a
 * rounded device frame with a notch + home indicator; the content
 * slot is transparent so the avatar stage / chat panel inside floats
 * on the host page's background.
 *
 * Sized at 360 × 740 CSS px by default (≈ iPhone 14). Hosts that want
 * a different size can pass `width` / `height` props OR transform-
 * scale the iframe in their own CSS.
 *
 * No assets — it's a `<div>` with rounded corners, inset shadow, and
 * two pseudo-elements for the chrome. Crisp at any zoom, themable via
 * the `bezel` prop.
 */
export interface IPhoneFrameProps {
  children: ReactNode;
  /** Width in CSS px. Default 360. */
  width?: number;
  /** Height in CSS px. Default 740. */
  height?: number;
  /** Bezel colour. Default near-black; pass `'white'` for a light theme. */
  bezel?: 'black' | 'white';
}

export function IPhoneFrame({
  children,
  width = 360,
  height = 740,
  bezel = 'black',
}: IPhoneFrameProps): ReactElement {
  const bezelColour = bezel === 'black' ? '#0b0b0d' : '#e5e5ea';
  const innerBorderRadius = 42;
  const outerBorderRadius = 50;

  return (
    <div
      style={{
        width,
        height,
        position: 'relative',
        borderRadius: outerBorderRadius,
        background: bezelColour,
        boxShadow: `
          0 25px 50px -12px rgba(0, 0, 0, 0.6),
          0 0 0 2px rgba(255, 255, 255, 0.05) inset,
          0 0 0 6px rgba(0, 0, 0, 0.6) inset
        `,
        padding: 8,
      }}
      aria-label="iPhone embed frame"
    >
      {/* Inner content area — transparent so the host bg shows through. */}
      <div
        style={{
          width: '100%',
          height: '100%',
          borderRadius: innerBorderRadius,
          overflow: 'hidden',
          position: 'relative',
          background: 'transparent',
        }}
      >
        {children}
        {/* Notch — narrow island at top centre. */}
        <div
          style={{
            position: 'absolute',
            top: 8,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 110,
            height: 28,
            borderRadius: 14,
            background: '#000',
            zIndex: 10,
            pointerEvents: 'none',
          }}
        />
        {/* Home indicator — bottom bar. */}
        <div
          style={{
            position: 'absolute',
            bottom: 8,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 134,
            height: 5,
            borderRadius: 3,
            background: bezel === 'black' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.4)',
            zIndex: 10,
            pointerEvents: 'none',
          }}
        />
      </div>
    </div>
  );
}
